import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

import { DefenseMobile } from 'modules/defense';

import asdaImage from '../../../main/assets/images/auvsi/asda.webp';
import asdaImageTablet from '../../../main/assets/images/auvsi/asda-tablet.webp';
import asdaImageMobile from '../../../main/assets/images/auvsi/asda-mobile.webp';

type Marker = {
  id: number;
  x: number;
  y: number;
  vector: {
    svg: {
      width: string;
      height: string;
      viewBox: string;
      path: string;
    };
    position: { x: number; y: number };
  };
  text: {
    title: string;
    description: string;
    position: { x: number; y: number };
    titleX: number;
    titleAnimStart: number;
    titleY: number;
    descriptionY: number;
    descriptionAnimStart: number;
  };
};

type ResponsiveConfig = {
  circleRadius: number;
  innerCircleRadius: number;
  lineWords: number;
  markers: Marker[];
  titleFontSize: string;
  descriptionFontSize: string;
  textContainerWidth: number;
  textContainerHeight: number;
  imageSrc: any;
};

const ANIMATION_DURATIONS = {
  TEXT_FADE: 300,
  VECTOR_DRAW: 400,
  CIRCLE_TRANSITION: 100,
  BETWEEN_ANIMATIONS: 50,
};

const titlePosition = {
  x: 5,
  y: 5,
};

export const ASDA = () => {
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [showVector, setShowVector] = useState(false);
  const [showText, setShowText] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const animationTimeout = useRef<NodeJS.Timeout>();

  const getResponsiveConfig = (): ResponsiveConfig => {
    if (windowWidth < 1000) {
      return {
        circleRadius: 20,
        innerCircleRadius: 8,
        titleFontSize: '28px',
        descriptionFontSize: '16px',
        imageSrc: asdaImageMobile,
        textContainerHeight: 160,
        textContainerWidth: 450,
        lineWords: 41,
        markers: [
          {
            id: 1,
            x: 25,
            y: 58,
            vector: {
              svg: {
                width: '603',
                height: '70',
                viewBox: '0 0 603 70',
                path: 'M1 251.5 L91 1 H574',
              },
              position: { x: 25, y: 37 },
            },
            text: {
              title: 'FPV.Pro',
              description:
                'Orqa FPV.Pro are rugged FPV goggles optimized for field operations, prioritizing simplicity and reliability. It features large, tactile switches operable with gloves, a durable and clean design, and ensures consistent performance in demanding, high-stakes mission environments.',
              position: { x: 38, y: 38 },
              titleX: 20,
              titleY: 32,
              descriptionY: 62,
              titleAnimStart: -10,
              descriptionAnimStart: 10,
            },
          },
          {
            id: 2,
            x: 56,
            y: 63,
            vector: {
              svg: {
                width: '560',
                height: '127',
                viewBox: '0 0 560 127',
                path: 'M424 132 L483 1 H0',
              },
              position: { x: 5, y: 52 },
            },
            text: {
              title: 'GCS-1',
              description:
                'Orqa GCS-1 Ground Control System is a complete solution for tactical FPV UAS operations. It includes all necessary components to ensure safe and reliable FPV mission deployments.',
              position: { x: 7, y: 34 },
              titleX: 20,
              titleY: 32,
              descriptionY: 62,
              titleAnimStart: -10,
              descriptionAnimStart: 10,
            },
          },
          {
            id: 3,
            x: 80,
            y: 33,
            vector: {
              svg: {
                width: '560',
                height: '127',
                viewBox: '0 0 560 127',
                path: 'M590 210 L470 10 H20',
              },
              position: { x: 10, y: 16 },
            },
            text: {
              title: 'MRM2-10F',
              description:
                'MRM2-10F is a lightweight, multi-role FPV multirotor UAV designed and built for versatility, effectiveness, and cost efficiency, with unparalleled EW resilience.',
              position: { x: 12.3, y: 18 },
              titleX: 20,
              titleY: 32,
              descriptionY: 62,
              titleAnimStart: -10,
              descriptionAnimStart: 10,
            },
          },
          {
            id: 4,
            x: 27,
            y: 85.5,
            vector: {
              svg: {
                width: '560',
                height: '127',
                viewBox: '0 0 560 127',
                path: 'M1 591 L81 1 H564',
              },
              position: { x: 27, y: 36 },
            },
            text: {
              title: 'Tac.Ctrl',
              description:
                'Tac.Ctrl is a tactical remote controller for first-person view (FPV) UAVs, designed for operator safety and built robustly to ensure reliable and precise operation in adverse field environments.',
              position: { x: 39.2, y: 37 },
              titleX: 20,
              titleY: 32,
              descriptionY: 62,
              titleAnimStart: -10,
              descriptionAnimStart: 10,
            },
          },
        ],
      };
    } else if (windowWidth < 1400) {
      return {
        circleRadius: 25,
        innerCircleRadius: 10,
        titleFontSize: '34px',
        descriptionFontSize: '18px',
        textContainerHeight: 200,
        textContainerWidth: 530,
        imageSrc: asdaImageTablet,
        lineWords: 42,
        markers: [
          {
            id: 1,
            x: 26.5,
            y: 40,
            vector: {
              svg: {
                width: '603',
                height: '70',
                viewBox: '0 0 603 70',
                path: 'M1 69 L72 1 L618 1',
              },
              position: { x: 26.2, y: 33 },
            },
            text: {
              title: 'FPV.Pro',
              description:
                'Orqa FPV.Pro is a rugged FPV goggles, designed for simplicity and ease of use in the field. With large and tactile glove-operable switches, robust and clean design, and simplified operation, the FPV.Pro has been designed for high-stakes missions.',
              position: { x: 32, y: 35 },
              titleX: 20,
              titleY: 40,
              descriptionY: 75,
              titleAnimStart: -20,
              descriptionAnimStart: 20,
            },
          },
          {
            id: 2,
            x: 54,
            y: 68,
            vector: {
              svg: {
                width: '560',
                height: '127',
                viewBox: '0 0 560 127',
                path: 'M1 289 L44 1 L581 1',
              },
              position: { x: 54, y: 40 },
            },
            text: {
              title: 'GCS-1',
              description:
                'Orqa GCS-1 Ground Control System is a complete solution for tactical FPV UAS operations. It contains everything that is required for safe and reliable FPV mission deployments.',
              position: { x: 57.5, y: 41.5 },
              titleX: 20,
              titleY: 40,
              descriptionY: 75,
              titleAnimStart: -20,
              descriptionAnimStart: 20,
            },
          },
          {
            id: 3,
            x: 80,
            y: 32,
            vector: {
              svg: {
                width: '560',
                height: '127',
                viewBox: '0 0 560 127',
                path: 'M699.5 1.5L546 70H0',
              },
              position: { x: 32, y: 31.5 },
            },
            text: {
              title: 'MRM2 - 10F',
              description:
                'MRM2-10F is a light-weight multi-role First Person View (FPV) multicopter UAV, designed and built for versatility, effectiveness, and cost efficiency with unparalleled EW resilience.',
              position: { x: 33, y: 40 },
              titleX: 20,
              titleY: 40,
              descriptionY: 75,
              titleAnimStart: -20,
              descriptionAnimStart: 20,
            },
          },
          {
            id: 4,
            x: 30,
            y: 80,
            vector: {
              svg: {
                width: '560',
                height: '127',
                viewBox: '0 0 560 127',
                path: 'M1 489 L138 1 H684',
              },
              position: { x: 30, y: 32 },
            },
            text: {
              title: 'Tac.Ctrl',
              description:
                'Tac.Ctrl is a tactical remote controller for First Person View (FPV) UAVs, designed for operator safety and robustly built for reliable and precise operation in adverse field environments.',
              position: { x: 40.2, y: 35 },
              titleX: 20,
              titleY: 40,
              descriptionY: 75,
              titleAnimStart: -20,
              descriptionAnimStart: 20,
            },
          },
        ],
      };
    } else {
      // This is the updated section for screens 1400px and up
      if (windowWidth >= 3000) {
        return {
          circleRadius: 58,
          innerCircleRadius: 20,
          titleFontSize: '64px',
          descriptionFontSize: '20px',
          textContainerWidth: 800,
          textContainerHeight: 230,
          imageSrc: asdaImage,
          lineWords: 60,
          markers: [
            {
              id: 1,
              x: 25,
              y: 40,
              vector: {
                svg: {
                  width: '500',
                  height: '70',
                  viewBox: '0 0 500 70',
                  path: 'M10 245 L280 10 L1200 10',
                },
                position: { x: 25, y: 28 },
              },
              text: {
                title: 'FPV.Pro',
                description:
                  'Orqa FPV.Pro is a rugged FPV goggles, designed for simplicity and ease of use in the field. With large and tactile glove-operable switches, robust and clean design, and simplified operation, the FPV.Pro has been designed for high-stakes missions.',
                position: { x: 34, y: 30 },
                titleX: 20,
                titleY: 80,
                descriptionY: 130,
                titleAnimStart: -30,
                descriptionAnimStart: 30,
              },
            },
            {
              id: 2,
              x: 54,
              y: 72,
              vector: {
                svg: {
                  width: '300',
                  height: '127',
                  viewBox: '0 0 300 127',
                  path: 'M 1435 400 L 1385 22 H 456',
                },
                position: { x: 16.5, y: 53 },
              },
              text: {
                title: 'GCS-1',
                description:
                  'Orqa GCS-1 Ground Control System is a complete solution for tactical FPV UAS operations. It contains everything that is required for safe and reliable FPV mission deployments.',
                position: { x: 29.5, y: 55.5 },
                titleX: 20,
                titleY: 80,
                descriptionY: 130,
                titleAnimStart: -30,
                descriptionAnimStart: 30,
              },
            },
            {
              id: 3,
              x: 80,
              y: 37,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M 1700 140 L 1320 20 H 400',
                },
                position: { x: 36, y: 30.5 },
              },
              text: {
                title: 'MRM2 - 10F',
                description:
                  'MRM2-10F is a light-weight multi-role First Person View (FPV) multicopter UAV, designed and built for versatility, effectiveness, and cost efficiency with unparalleled EW resilience.',
                position: { x: 48, y: 33 },
                titleX: 20,
                titleY: 80,
                descriptionY: 130,
                titleAnimStart: -30,
                descriptionAnimStart: 30,
              },
            },
            {
              id: 4,
              x: 30,
              y: 85,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M 3 1040 L 275 61 L 1150 61',
                },
                position: { x: 30, y: 36 },
              },
              text: {
                title: 'Tac.Ctrl',
                description:
                  'Tac.Ctrl is a tactical remote controller for First Person View (FPV) UAVs, designed for operator safety and robustly built for reliable and precise operation in adverse field environments.',
                position: { x: 38, y: 40 },
                titleX: 20,
                titleY: 80,
                descriptionY: 130,
                titleAnimStart: -30,
                descriptionAnimStart: 30,
              },
            },
          ],
        };
      } else if (windowWidth >= 2500) {
        return {
          circleRadius: 58,
          innerCircleRadius: 20,
          titleFontSize: '72px',
          descriptionFontSize: '32px',
          textContainerWidth: 1200,
          textContainerHeight: 310,
          lineWords: 58,
          imageSrc: asdaImage,
          markers: [
            {
              id: 1,
              x: 25,
              y: 40,
              vector: {
                svg: {
                  width: '603',
                  height: '70',
                  viewBox: '0 0 603 70',
                  path: 'M10 245 L280 10 L1600 10',
                },
                position: { x: 25, y: 28 },
              },
              text: {
                title: 'FPV.Pro',
                description:
                  'Orqa FPV.Pro is a rugged FPV goggles, designed for simplicity and ease of use in the field. With large and tactile glove-operable switches, robust and clean design, and simplified operation, the FPV.Pro has been designed for high-stakes missions.',
                position: { x: 34, y: 30 },
                titleX: 20,
                titleY: 90,
                descriptionY: 150,
                titleAnimStart: -30,
                descriptionAnimStart: 30,
              },
            },
            {
              id: 2,
              x: 54,
              y: 72,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M1435 400 L1385 22 H65',
                },
                position: { x: 16.5, y: 53 },
              },
              text: {
                title: 'GCS-1',
                description:
                  'Orqa GCS-1 Ground Control System is a complete solution for tactical FPV UAS operations. It contains everything that is required for safe and reliable FPV mission deployments.',
                position: { x: 20, y: 55.5 },
                titleX: 20,
                titleY: 100,
                descriptionY: 160,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
            {
              id: 3,
              x: 80,
              y: 37,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M1700 140 L1320 20 H20',
                },
                position: { x: 36, y: 30.5 },
              },
              text: {
                title: 'MRM2 - 10F',
                description:
                  'MRM2-10F is a light-weight multi-role First Person View (FPV) multicopter UAV, designed and built for versatility, effectiveness, and cost efficiency with unparalleled EW resilience.',
                position: { x: 38, y: 33 },
                titleX: 20,
                titleY: 100,
                descriptionY: 160,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
            {
              id: 4,
              x: 30,
              y: 85,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M3 1040 L286.63 3 L1669.5 3',
                },
                position: { x: 30, y: 36 },
              },
              text: {
                title: 'Tac.Ctrl',
                description:
                  'Tac.Ctrl is a tactical remote controller for First Person View (FPV) UAVs, designed for operator safety and robustly built for reliable and precise operation in adverse field environments.',
                position: { x: 40, y: 37.5 },
                titleX: 20,
                titleY: 100,
                descriptionY: 160,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
          ],
        };
      } else {
        // Fallback for 1400px to 2499px
        return {
          circleRadius: 58,
          innerCircleRadius: 20,
          titleFontSize: '86px',
          descriptionFontSize: '40px',
          textContainerWidth: 1200,
          textContainerHeight: 410,
          imageSrc: asdaImage,
          lineWords: 45,
          markers: [
            {
              id: 1,
              x: 25,
              y: 40,
              vector: {
                svg: {
                  width: '603',
                  height: '70',
                  viewBox: '0 0 603 70',
                  path: 'M10 245 L280 10 L1600 10',
                },
                position: { x: 25, y: 28 },
              },
              text: {
                title: 'FPV.Pro',
                description:
                  'Orqa FPV.Pro is a rugged FPV goggles, designed for simplicity and ease of use in the field. With large and tactile glove-operable switches, robust and clean design, and simplified operation, the FPV.Pro has been designed for high-stakes missions.',
                position: { x: 34, y: 30 },
                titleX: 20,
                titleY: 100,
                descriptionY: 170,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
            {
              id: 2,
              x: 54,
              y: 72,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M1435 400 L1385 22 H65',
                },
                position: { x: 16.5, y: 53 },
              },
              text: {
                title: 'GCS-1',
                description:
                  'Orqa GCS-1 Ground Control System is a complete solution for tactical FPV UAS operations. It contains everything that is required for safe and reliable FPV mission deployments.',
                position: { x: 20, y: 55.5 },
                titleX: 20,
                titleY: 100,
                descriptionY: 170,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
            {
              id: 3,
              x: 80,
              y: 37,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M1700 140 L1320 20 H20',
                },
                position: { x: 36, y: 30.5 },
              },
              text: {
                title: 'MRM2 - 10F',
                description:
                  'MRM2-10F is a light-weight multi-role First Person View (FPV) multicopter UAV, designed and built for versatility, effectiveness, and cost efficiency with unparalleled EW resilience.',
                position: { x: 38, y: 33 },
                titleX: 20,
                titleY: 100,
                descriptionY: 170,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
            {
              id: 4,
              x: 30,
              y: 85,
              vector: {
                svg: {
                  width: '560',
                  height: '127',
                  viewBox: '0 0 560 127',
                  path: 'M3 1040 L286.63 3 L1669.5 3',
                },
                position: { x: 30, y: 36 },
              },
              text: {
                title: 'Tac.Ctrl',
                description:
                  'Tac.Ctrl is a tactical remote controller for First Person View (FPV) UAVs, designed for operator safety and robustly built for reliable and precise operation in adverse field environments.',
                position: { x: 40, y: 37.5 },
                titleX: 20,
                titleY: 100,
                descriptionY: 170,
                titleAnimStart: -20,
                descriptionAnimStart: 20,
              },
            },
          ],
        };
      }
    }
  };
  const {
    circleRadius,
    innerCircleRadius,
    markers,
    titleFontSize,
    descriptionFontSize,
    textContainerHeight,
    textContainerWidth,
    lineWords,
    imageSrc,
  } = getResponsiveConfig();

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.target as HTMLImageElement;
      setImageSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    },
    [],
  );

  const getCoordinates = (xPercent: number, yPercent: number) => {
    return {
      x: (xPercent / 100) * imageSize.width,
      y: (yPercent / 100) * imageSize.height,
    };
  };

  const openIndicator = (index: number) => {
    if (animationTimeout.current) {
      clearTimeout(animationTimeout.current);
    }

    setActiveIndex(index);
    setShowVector(false);
    setShowText(false);
    setIsClosing(false);

    animationTimeout.current = setTimeout(() => {
      setShowVector(true);
      animationTimeout.current = setTimeout(() => {
        setShowText(true);
      }, ANIMATION_DURATIONS.VECTOR_DRAW + ANIMATION_DURATIONS.BETWEEN_ANIMATIONS);
    }, ANIMATION_DURATIONS.CIRCLE_TRANSITION);
  };

  const closeIndicator = () => {
    if (animationTimeout.current) {
      clearTimeout(animationTimeout.current);
    }

    setIsClosing(true);
    setShowText(false);

    animationTimeout.current = setTimeout(() => {
      setShowVector(false);
      animationTimeout.current = setTimeout(() => {
        setIsClosing(false);
        setActiveIndex(null);
      }, ANIMATION_DURATIONS.VECTOR_DRAW);
    }, ANIMATION_DURATIONS.TEXT_FADE);
  };

  const handleIndicatorClick = (index: number) => {
    if (activeIndex === index) {
      closeIndicator();
      return;
    }

    if (activeIndex !== null) {
      closeIndicator();
      animationTimeout.current = setTimeout(() => {
        openIndicator(index);
      }, ANIMATION_DURATIONS.TEXT_FADE + ANIMATION_DURATIONS.VECTOR_DRAW);
    } else {
      openIndicator(index);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (activeIndex !== null) {
        closeIndicator();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeIndex]);

  const wrapText = useCallback((text: string, maxCharsPerLine: number) => {
    const words = text.split(' ');
    const lines = [];
    let line: any[] = [];

    words.forEach(word => {
      const testLine = [...line, word].join(' ');
      if (testLine.length > maxCharsPerLine) {
        lines.push(line.join(' '));
        line = [word];
      } else {
        line = [...line, word];
      }
    });

    if (line.length) lines.push(line.join(' '));
    return lines;
  }, []);

  return (
    <>
      {windowWidth < 601 ? (
        <DefenseMobile />
      ) : (
        <div
          style={{
            position: 'relative',
            minWidth: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Main Image Container */}
          <div style={{ position: 'relative' }}>
            <img
              src={imageSrc}
              alt="Device"
              style={{
                width: '100%',
                maxHeight: '100%',
                display: 'block',
              }}
              onLoad={handleImageLoad}
            />

            {/* SVG Overlay */}
            {imageSize.width > 0 && (
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <foreignObject
                  x={getCoordinates(titlePosition.x, titlePosition.y).x}
                  y={getCoordinates(titlePosition.x, titlePosition.y).y}
                  style={{
                    pointerEvents: 'none',
                    width:
                      windowWidth < 1000
                        ? '100%'
                        : windowWidth < 1400
                        ? '75%'
                        : '50%',
                    height:
                      windowWidth < 1000
                        ? '100%'
                        : windowWidth < 1400
                        ? '75%'
                        : '50%',
                  }}
                >
                  <div
                    style={{
                      color: 'white',
                      fontSize:
                        windowWidth >= 3000
                          ? '80px'
                          : windowWidth >= 2500
                          ? '120px'
                          : windowWidth >= 1400
                          ? '128px'
                          : windowWidth >= 1000
                          ? '64px'
                          : '48px',
                      fontWeight: 'bold',
                    }}
                  >
                    <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
                      Rugged Tactical
                    </span>
                    <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
                      Defense Solution
                    </span>
                  </div>
                </foreignObject>
                {/* Interactive markers */}
                {markers.map((marker, index) => {
                  const markerCoords = getCoordinates(marker.x, marker.y);
                  const vectorCoords = getCoordinates(
                    marker.vector.position.x,
                    marker.vector.position.y,
                  );
                  const textCoords = getCoordinates(
                    marker.text.position.x,
                    marker.text.position.y,
                  );

                  return (
                    <React.Fragment key={marker.id}>
                      {/* Marker circle */}
                      <g
                        transform={`translate(${markerCoords.x},${markerCoords.y})`}
                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        onClick={() => handleIndicatorClick(index)}
                      >
                        {/* White circle */}
                        <motion.circle
                          cx={0}
                          cy={0}
                          r={circleRadius}
                          fill="url(#paint0_radial_123_925)"
                          initial={{ opacity: 1 }}
                          animate={{
                            opacity: activeIndex === index ? 0 : 1,
                            scale: activeIndex === index ? 0.8 : 1,
                          }}
                          transition={{
                            duration:
                              ANIMATION_DURATIONS.CIRCLE_TRANSITION / 1000,
                          }}
                        />
                        <circle
                          cx={0}
                          cy={0}
                          r={innerCircleRadius}
                          fill="white"
                        />

                        {/* Red circle */}
                        <motion.circle
                          cx={0}
                          cy={0}
                          r={circleRadius}
                          fill="url(#paint0_radial_127_1107)"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity: activeIndex === index ? 1 : 0,
                            scale: activeIndex === index ? 1 : 0,
                          }}
                          transition={{
                            duration:
                              ANIMATION_DURATIONS.CIRCLE_TRANSITION / 1000,
                            ease: 'backOut',
                          }}
                        />
                        <circle
                          cx={0}
                          cy={0}
                          r={innerCircleRadius}
                          fill="#EE2737"
                        />
                      </g>

                      {/* Vector line */}
                      {activeIndex === index && showVector && (
                        <motion.path
                          d={marker.vector.svg.path}
                          fill="none"
                          stroke="#EE2737"
                          strokeWidth="5.06546"
                          transform={`translate(${vectorCoords.x},${vectorCoords.y})`}
                          initial={{ pathLength: 0 }}
                          animate={{
                            pathLength: isClosing ? 0 : 1,
                          }}
                          transition={{
                            duration: ANIMATION_DURATIONS.VECTOR_DRAW / 1000,
                            ease: 'easeInOut',
                          }}
                        />
                      )}

                      {activeIndex === index && (
                        <g
                          transform={`translate(${textCoords.x},${textCoords.y})`}
                        >
                          {/* Background with blur effect */}
                          <motion.rect
                            width={textContainerWidth}
                            height={
                              activeIndex !== 0
                                ? textContainerHeight
                                : textContainerHeight + 50
                            }
                            initial={{ opacity: 0 }}
                            animate={{
                              opacity: showText ? 1 : 0,
                            }}
                            transition={{
                              duration: ANIMATION_DURATIONS.TEXT_FADE / 1000,
                            }}
                            fill="rgba(0, 0, 0, 0.6)"
                            rx="8"
                            ry="8"
                          />

                          {/* Text content */}
                          <motion.g
                            initial={{ opacity: 0 }}
                            animate={{
                              opacity: showText ? 1 : 0,
                            }}
                            transition={{
                              duration: ANIMATION_DURATIONS.TEXT_FADE / 1000,
                            }}
                          >
                            {/* Title */}
                            <motion.text
                              initial={{ y: marker.text.titleAnimStart }}
                              animate={{ y: 0 }}
                              transition={{ duration: 0.2, delay: 0.6 }}
                              style={{
                                fontWeight: 'bold',
                                fontSize: titleFontSize,
                                fill: 'white',
                              }}
                              y={marker.text.titleY}
                              x={marker.text.titleX}
                            >
                              {marker.text.title}
                            </motion.text>

                            <motion.text
                              initial={{ y: marker.text.descriptionAnimStart }}
                              animate={{ y: 0 }}
                              transition={{ duration: 0.2, delay: 0.6 }}
                              style={{
                                fontSize: descriptionFontSize,
                                fill: 'white',
                              }}
                              x={marker.text.titleX}
                              y={marker.text.descriptionY}
                            >
                              {wrapText(marker.text.description, lineWords).map(
                                (line, i) => (
                                  <tspan
                                    key={i}
                                    x={20}
                                    dy={
                                      i === 0
                                        ? 0
                                        : parseFloat(descriptionFontSize) * 1.2
                                    }
                                  >
                                    {line}
                                  </tspan>
                                ),
                              )}
                            </motion.text>
                          </motion.g>
                        </g>
                      )}
                    </React.Fragment>
                  );
                })}

                <defs>
                  <radialGradient
                    id="paint0_radial_123_925"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform={`rotate(90) scale(${circleRadius})`}
                  >
                    <motion.stop
                      stopColor="white"
                      animate={{ stopOpacity: [0.1, 0.3, 0.1] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    <motion.stop
                      offset="1"
                      stopColor="white"
                      animate={{ stopOpacity: [0.1, 0.3, 0.1] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: 0.2,
                      }}
                    />
                  </radialGradient>

                  <radialGradient
                    id="paint0_radial_127_1107"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform={`rotate(90) scale(${circleRadius})`}
                  >
                    <stop stopColor="#F1274C" stopOpacity="0.3" />
                    <stop offset="1" stopColor="#F1274C" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>
            )}
          </div>
        </div>
      )}
    </>
  );
};
